"use client"

import { useEffect, useState, useCallback } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { Bell, ChevronRight, AlertTriangle, Info, Megaphone, CheckCheck, CheckCircle, XCircle, KeyRound, Gauge } from "lucide-react"
import { Button } from "@/components/ui/button"
import { api } from "@/lib/api"
import type { NotificationsResponse } from "@/lib/types"
import { transformNotifications, type Notice } from "@/lib/transforms"

const getNoticeIcon = (type: string) => {
  switch (type) {
    case "maintenance":
      return <AlertTriangle className="h-4 w-4 text-warning" />
    case "update":
      return <Megaphone className="h-4 w-4 text-primary" />
    case "sync_success":
      return <CheckCircle className="h-4 w-4 text-green-600" />
    case "sync_error":
      return <XCircle className="h-4 w-4 text-destructive" />
    case "token_expired":
      return <KeyRound className="h-4 w-4 text-orange-500" />
    case "quota_warning":
      return <Gauge className="h-4 w-4 text-yellow-600" />
    default:
      return <Info className="h-4 w-4 text-muted-foreground" />
  }
}

export function NoticesWidget() {
  const [notices, setNotices] = useState<Notice[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.get<NotificationsResponse>("/notifications")
      .then((res) => setNotices(transformNotifications(res.data)))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const markAsRead = useCallback(async (id: number) => {
    try {
      await api.put<{ message: string }>(`/notifications/${id}/read`, {})
      setNotices((prev) => prev.map((n) => (n.id === id ? { ...n, isNew: false } : n)))
    } catch (e) {
      console.error("Failed to mark as read:", e)
    }
  }, [])

  const markAllAsRead = useCallback(async () => {
    try {
      await api.put<{ message: string }>("/notifications/read-all", {})
      setNotices((prev) => prev.map((n) => ({ ...n, isNew: false })))
    } catch (e) {
      console.error("Failed to mark all as read:", e)
    }
  }, [])

  const newCount = notices.filter((n) => n.isNew).length

  return (
    <Card className="col-span-full lg:col-span-1">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">お知らせ</CardTitle>
          </div>
          <Badge variant="secondary" className="text-xs">
            {loading ? "..." : `${newCount}件の新着`}
          </Badge>
        </div>
        <CardDescription>システムからの重要なお知らせ</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : notices.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">お知らせはありません</p>
        ) : (
          notices.map((notice) => (
            <div
              key={notice.id}
              className="group cursor-pointer rounded-lg border border-border bg-card p-3 transition-colors hover:bg-secondary/50"
              onClick={() => notice.isNew && markAsRead(notice.id)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    {getNoticeIcon(notice.type)}
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-foreground">
                        {notice.title}
                      </p>
                      {notice.isNew && (
                        <Badge className="h-5 bg-destructive text-destructive-foreground text-[10px]">
                          NEW
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {notice.description}
                    </p>
                    <p className="text-xs text-muted-foreground/70">
                      {notice.date}
                    </p>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
              </div>
            </div>
          ))
        )}

        {newCount > 0 && (
          <Button
            variant="ghost"
            className="w-full text-sm text-muted-foreground hover:text-foreground"
            onClick={markAllAsRead}
          >
            <CheckCheck className="mr-1 h-4 w-4" />
            すべて既読にする
          </Button>
        )}

        <Button variant="ghost" className="w-full text-sm text-muted-foreground hover:text-foreground">
          すべてのお知らせを見る
          <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  )
}
